/**
 * District colors: one shared resolver so the map fill, the palette, and
 * the stats table always agree on which color belongs to which district.
 *
 * Tenant configs may supply fewer colors than districts (or none at all);
 * the built-in palette fills the gaps. This fixed a real inconsistency:
 * the map used to render districts past the end of `districts.colors` as
 * unassigned, while the store fell back to the default palette.
 */

/**
 * Built-in district palette ("Civic Atlas"): derived from the Okabe-Ito
 * colorblind-safe set, tuned to stay distinct at map fill opacity over a
 * street basemap. Blue / orange / green / vermillion lead because they stay
 * separable under the common color-vision deficiencies. Used when the tenant
 * config omits `districts.colors` or supplies fewer than needed.
 *
 * Keep in sync with backend/app/seed_local.py CIVIC_DISTRICT_COLORS.
 */
export const DEFAULT_DISTRICT_COLORS: readonly string[] = [
  '#2F6DB5', // harbor blue
  '#E8912D', // marigold
  '#1E9E78', // bluish green
  '#D1543A', // vermillion
  '#8E63B8', // violet
  '#4AA8D8', // sky
  '#D9B526', // ochre
  '#B565A7', // orchid
  '#5E8C31', // olive
  '#9C6644', // sienna
  '#3F4FB8', // indigo
  '#C2457A', // raspberry
];

/** Last-resort fill when every palette is exhausted (should not happen). */
export const FALLBACK_DISTRICT_COLOR = '#78716c';

/**
 * Resolve one color per district index: tenant colors first, then the
 * built-in palette cycling. Always returns exactly `districtCount` colors.
 */
export function resolveDistrictColors(
  districtCount: number,
  tenantColors: readonly string[] | undefined,
): string[] {
  const colors: string[] = [];
  for (let i = 0; i < districtCount; i += 1) {
    colors.push(
      tenantColors?.[i] ??
        DEFAULT_DISTRICT_COLORS[i % DEFAULT_DISTRICT_COLORS.length] ??
        FALLBACK_DISTRICT_COLOR,
    );
  }
  return colors;
}

/**
 * Resolve display labels: tenant labels first, then "District N".
 * Always returns exactly `districtCount` labels.
 */
export function resolveDistrictLabels(
  districtCount: number,
  tenantLabels: readonly string[] | undefined,
): string[] {
  const labels: string[] = [];
  for (let i = 0; i < districtCount; i += 1) {
    labels.push(tenantLabels?.[i] ?? `District ${i + 1}`);
  }
  return labels;
}

/**
 * Pick black or white text for a label drawn on `hex` (WCAG relative
 * luminance). Used for district number chips.
 */
export function readableTextOn(hex: string): '#ffffff' | '#1C2A33' {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m || !m[1]) return '#ffffff';
  const n = parseInt(m[1], 16);
  const channel = (v: number): number => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const lum =
    0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
  return lum > 0.42 ? '#1C2A33' : '#ffffff';
}
